/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { $createCodeNode, $isCodeNode, CODE_LANGUAGE_FRIENDLY_NAME_MAP, CODE_LANGUAGE_MAP, getLanguageFriendlyName } from "@lexical/code";
import { $isLinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";
import { $isListNode, INSERT_CHECK_LIST_COMMAND, INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND, ListNode } from "@lexical/list";
import { INSERT_EMBED_COMMAND } from "@lexical/react/LexicalAutoEmbedPlugin";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $isDecoratorBlockNode } from "@lexical/react/LexicalDecoratorBlockNode";
import { INSERT_HORIZONTAL_RULE_COMMAND } from "components/NewEditor/nodes/HorizontalRuleNode";
import { $createHeadingNode, $isHeadingNode, HeadingTagType } from "@lexical/rich-text";
import { $getSelectionStyleValueForProperty, $isParentElementRTL, $patchStyleText, $setBlocksType } from "@lexical/selection";
import { $isTableNode, $isTableSelection } from "@lexical/table";
import { $findMatchingParent, $getNearestBlockElementAncestorOrThrow, $getNearestNodeOfType, mergeRegister } from "@lexical/utils";
import { $createSlidParagraphNode } from "components/NewEditor/nodes/SlidParagraphNode";
import {
  $getNodeByKey,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $isRootOrShadowRoot,
  $isTextNode,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_NORMAL,
  ElementFormatType,
  FORMAT_TEXT_COMMAND,
  KEY_MODIFIER_COMMAND,
  LexicalEditor,
  NodeKey,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  UNDO_COMMAND,
} from "lexical";
import { Dispatch, useCallback, useEffect, useState } from "react";
import * as React from "react";
import { IS_APPLE } from "../../utils/environment";

import useModal from "../../hooks/useModal";
import DropDown, { DropDownItem } from "../../ui/DropDown";
import { DropdownTextColorPicker, DropdownBgColorPicker } from "../../ui/DropdownColorPicker";
import { getSelectedNode } from "../../utils/getSelectedNode";
import { sanitizeUrl } from "../../utils/url";
import { InsertEquationDialog } from "../EquationsPlugin";
import { INSERT_IMAGE_COMMAND, InsertImageDialog, InsertImagePayload } from "../ImagesPlugin";
import { InsertInlineImageDialog } from "../InlineImagePlugin";
import { InsertTableDialog } from "../TablePlugin";
import { InsertLinkPreviewDialog } from "../SlidLinkPreviewPlugin";
import FontSize from "./fontSize";
import styled from "styled-components";
import { Tooltip, Typography11, Typography13 } from "@slid/slid-ips";
import { useTranslation } from "react-i18next";
import { ReactComponent as ParagraphIcon } from "../../images/icons/dropdown-paragraph.svg";
import { ReactComponent as Heading1Icon } from "../../images/icons/dropdown-heading1.svg";
import { ReactComponent as Heading2Icon } from "../../images/icons/dropdown-heading2.svg";
import { ReactComponent as Heading3Icon } from "../../images/icons/dropdown-heading3.svg";
import { ReactComponent as BulletListIcon } from "../../images/icons/dropdown-bullet.svg";
import { ReactComponent as NumberedListIcon } from "../../images/icons/dropdown-number.svg";
import { ReactComponent as CheckListIcon } from "../../images/icons/dropdown-check.svg";
import { ReactComponent as CodeIcon } from "../../images/icons/dropdown-code.svg";
import { ReactComponent as LabelParagraphIcon } from "../../images/icons/paragraph.svg";
import { ReactComponent as LabelHeading1Icon } from "../../images/icons/heading1.svg";
import { ReactComponent as LabelHeading2Icon } from "../../images/icons/heading2.svg";
import { ReactComponent as LabelHeading3Icon } from "../../images/icons/heading3.svg";
import { ReactComponent as LabelBulletListIcon } from "../../images/icons/bullet.svg";
import { ReactComponent as LabelNumberedListIcon } from "../../images/icons/number.svg";
import { ReactComponent as LabelCheckListIcon } from "../../images/icons/check.svg";
import { ReactComponent as LabelCodeIcon } from "../../images/icons/code.svg";
import { ReactComponent as AddIcon } from "../../images/icons/add.svg";
import { ReactComponent as TableIcon } from "../../images/icons/table.svg";
import { ReactComponent as DividerIcon } from "../../images/icons/divider.svg";
import { ReactComponent as ImageIcon } from "../../images/icons/image.svg";
import { ReactComponent as LinkPreviewIcon } from "../../images/icons/link-preview.svg";
import { ReactComponent as CodeBlockIcon } from "../../images/icons/code-block.svg";
import { ReactComponent as MathIcon } from "../../images/icons/math.svg";
import { ReactComponent as ImportPdfIcon } from "../../images/icons/import-pdf.svg";
import { ReactComponent as FloatingParagraphIcon } from "../../images/icons/floating-paragraph.svg";
import { ReactComponent as FloatingHeading1Icon } from "../../images/icons/floating-heading1.svg";
import { ReactComponent as FloatingHeading2Icon } from "../../images/icons/floating-heading2.svg";
import { ReactComponent as FloatingHeading3Icon } from "../../images/icons/floating-heading3.svg";
import { ReactComponent as FloatingBulletListIcon } from "../../images/icons/floating-bullet.svg";
import { ReactComponent as FloatingNumberedListIcon } from "../../images/icons/floating-number.svg";
import { ReactComponent as FloatingCheckListIcon } from "../../images/icons/floating-check.svg";
import { ReactComponent as FloatingCodeIcon } from "../../images/icons/floating-code.svg";

import { BoldIcon } from "components/NewEditor/components/icons/BoldIcon";
import { ItalicIcon } from "components/NewEditor/components/icons/ItalicIcon";
import { UnderlineIcon } from "components/NewEditor/components/icons/UnderlineIcon";
import { InlineCodeIcon } from "components/NewEditor/components/icons/InlineCodeIcon";
import { InlineLinkIcon } from "components/NewEditor/components/icons/InlineLinkIcon";
import { TextColorIcon } from "components/NewEditor/components/icons/TextColorIcon";
import { MarkerIcon } from "components/NewEditor/components/icons/MarkerIcon";
import usePlaceholder from "components/NewEditor/hooks/usePlaceholder";
import useEditorStore from "store/useEditorStore";
import { isMacOs } from "react-device-detect";
import useEditorPDFUpload from "hooks/useEditorPDFUpload";
import { SHORTCUTS } from "../ShortcutsPlugin/shortcuts";

export const blockTypeToBlockName = {
  bullet: "Bulleted List",
  check: "Check List",
  code: "Code Block",
  h1: "Heading 1",
  h2: "Heading 2",
  h3: "Heading 3",
  h4: "Heading 4",
  h5: "Heading 5",
  h6: "Heading 6",
  number: "Numbered List",
  "slid-paragraph": "Normal",
};

const blockTypeToBlockIcon = {
  bullet: <LabelBulletListIcon />,
  check: <LabelCheckListIcon />,
  code: <LabelCodeIcon />,
  h1: <LabelHeading1Icon />,
  h2: <LabelHeading2Icon />,
  h3: <LabelHeading3Icon />,
  number: <LabelNumberedListIcon />,
  "slid-paragraph": <LabelParagraphIcon />,
};

const rootTypeToRootName = {
  root: "Root",
  table: "Table",
};

function getCodeLanguageOptions(): [string, string][] {
  const options: [string, string][] = [];

  for (const [lang, friendlyName] of Object.entries(CODE_LANGUAGE_FRIENDLY_NAME_MAP)) {
    options.push([lang, friendlyName]);
  }

  return options;
}

const CODE_LANGUAGE_OPTIONS = getCodeLanguageOptions();

function dropDownActiveClass(active: boolean) {
  if (active) {
    return "active dropdown-item-active";
  } else {
    return "";
  }
}

export function BlockFormatDropDown({
  editor,
  blockType,
  disabled = false,
  isFloatingToolbar = false,
}: {
  blockType: keyof typeof blockTypeToBlockName;
  editor: LexicalEditor;
  disabled?: boolean;
  isFloatingToolbar?: boolean;
}): JSX.Element {
  const { t } = useTranslation("EditorToolbar");

  const formatParagraph = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createSlidParagraphNode());
      }
    });
  };

  const formatHeading = (headingSize: HeadingTagType) => {
    if (blockType !== headingSize) {
      editor.update(() => {
        const selection = $getSelection();
        $setBlocksType(selection, () => $createHeadingNode(headingSize));
      });
    }
  };

  const formatBulletList = () => {
    if (blockType !== "bullet") {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    } else {
      formatParagraph();
    }
  };

  const formatCheckList = () => {
    if (blockType !== "check") {
      editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined);
    } else {
      formatParagraph();
    }
  };

  const formatNumberedList = () => {
    if (blockType !== "number") {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    } else {
      formatParagraph();
    }
  };

  const formatInlineCode = () => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, "code");
  };

  const formatCode = () => {
    if (blockType !== "code") {
      editor.update(() => {
        let selection = $getSelection();

        if (selection !== null) {
          if (selection.isCollapsed()) {
            $setBlocksType(selection, () => $createCodeNode());
          } else {
            const textContent = selection.getTextContent();
            const codeNode = $createCodeNode();
            selection.insertNodes([codeNode]);
            selection = $getSelection();
            if ($isRangeSelection(selection)) {
              selection.insertRawText(textContent);
            }
          }
        }
      });
    }
  };

  return (
    <DropDown
      disabled={disabled}
      buttonClassName={"toolbar-item block-controls"}
      buttonIconClassName={"icon block-type " + blockType}
      buttonLabelIcon={blockTypeToBlockIcon[blockType]}
      buttonAriaLabel="Formatting options for text style"
      isFloatingToolbar={isFloatingToolbar}
      tooltipTitle={t("convert-to")}
    >
      {isFloatingToolbar && <Typography11 text={t("convert-to")} color={"--gray9"} type="span" style={{ padding: "0 12px 4px 12px" }} />}
      <DropDownItem
        className={"item " + dropDownActiveClass(blockType === "slid-paragraph") + (isFloatingToolbar ? " floating-toolbar-item" : "")}
        onClick={formatParagraph}
        isActive={blockType === "slid-paragraph" && isFloatingToolbar}
      >
        {isFloatingToolbar ? <FloatingParagraphIcon /> : <ParagraphIcon />}
        <Typography13 text={t("paragraph")} color={"--gray15"} type="span" />
      </DropDownItem>
      <DropDownItem
        className={"item " + dropDownActiveClass(blockType === "h1") + (isFloatingToolbar ? " floating-toolbar-item" : "")}
        onClick={() => formatHeading("h1")}
        isActive={blockType === "h1" && isFloatingToolbar}
      >
        {isFloatingToolbar ? <FloatingHeading1Icon /> : <Heading1Icon />}
        <Typography13 text={t("heading1")} color={"--gray15"} type="span" />
      </DropDownItem>
      <DropDownItem
        className={"item " + dropDownActiveClass(blockType === "h2") + (isFloatingToolbar ? " floating-toolbar-item" : "")}
        onClick={() => formatHeading("h2")}
        isActive={blockType === "h2" && isFloatingToolbar}
      >
        {isFloatingToolbar ? <FloatingHeading2Icon /> : <Heading2Icon />}
        <Typography13 text={t("heading2")} color={"--gray15"} type="span" />
      </DropDownItem>
      <DropDownItem
        className={"item " + dropDownActiveClass(blockType === "h3") + (isFloatingToolbar ? " floating-toolbar-item" : "")}
        onClick={() => formatHeading("h3")}
        isActive={blockType === "h3" && isFloatingToolbar}
      >
        {isFloatingToolbar ? <FloatingHeading3Icon /> : <Heading3Icon />}
        <Typography13 text={t("heading3")} color={"--gray15"} type="span" />
      </DropDownItem>
      <HorizontalDivider />
      <DropDownItem
        className={"item " + dropDownActiveClass(blockType === "bullet") + (isFloatingToolbar ? " floating-toolbar-item" : "")}
        onClick={formatBulletList}
        isActive={blockType === "bullet" && isFloatingToolbar}
      >
        {isFloatingToolbar ? <FloatingBulletListIcon /> : <BulletListIcon />}
        <Typography13 text={t("bulletList")} color={"--gray15"} type="span" />
      </DropDownItem>
      <DropDownItem
        className={"item " + dropDownActiveClass(blockType === "number") + (isFloatingToolbar ? " floating-toolbar-item" : "")}
        onClick={formatNumberedList}
        isActive={blockType === "number" && isFloatingToolbar}
      >
        {isFloatingToolbar ? <FloatingNumberedListIcon /> : <NumberedListIcon />}
        <Typography13 text={t("numberedList")} color={"--gray15"} type="span" />
      </DropDownItem>
      <DropDownItem
        className={"item " + dropDownActiveClass(blockType === "check") + (isFloatingToolbar ? " floating-toolbar-item" : "")}
        onClick={formatCheckList}
        isActive={blockType === "check" && isFloatingToolbar}
      >
        {isFloatingToolbar ? <FloatingCheckListIcon /> : <CheckListIcon />}
        <Typography13 text={t("checkList")} color={"--gray15"} type="span" />
      </DropDownItem>
      {isFloatingToolbar && (
        <>
          <HorizontalDivider />
          <DropDownItem className={"item " + dropDownActiveClass(blockType === "code") + (isFloatingToolbar ? " floating-toolbar-item" : "")} onClick={formatCode}>
            <FloatingCodeIcon />
            <Typography13 text={t("code-block")} color={"--gray15"} type="span" />
          </DropDownItem>
        </>
      )}
    </DropDown>
  );
}

export default function ToolbarPlugin({ setIsLinkEditMode }: { setIsLinkEditMode: Dispatch<boolean> }): JSX.Element {
  const [editor] = useLexicalComposerContext();
  const [activeEditor, setActiveEditor] = useState(editor);
  const [blockType, setBlockType] = useState<keyof typeof blockTypeToBlockName>("slid-paragraph");
  const [rootType, setRootType] = useState<keyof typeof rootTypeToRootName>("root");
  const [selectedElementKey, setSelectedElementKey] = useState<NodeKey | null>(null);
  const [fontColor, setFontColor] = useState<string>("#000000");
  const [bgColor, setBgColor] = useState<string>("#fff");
  const [elementFormat, setElementFormat] = useState<ElementFormatType>("left");
  const [isLink, setIsLink] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isCode, setIsCode] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [modal, showModal] = useModal();
  const [codeLanguage, setCodeLanguage] = useState<string>("");
  const [isEditable, setIsEditable] = useState(() => editor.isEditable());
  const { t } = useTranslation(["EditorToolbar", "EditorModal"]);
  const { handleClickPDFUpload } = useEditorPDFUpload();
  const { focusEditor } = useEditorStore();

  const $updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      let element =
        anchorNode.getKey() === "root"
          ? anchorNode
          : $findMatchingParent(anchorNode, (e) => {
              const parent = e.getParent();
              return parent !== null && $isRootOrShadowRoot(parent);
            });

      if (element === null) {
        element = anchorNode.getTopLevelElementOrThrow();
      }

      const elementKey = element.getKey();
      const elementDOM = activeEditor.getElementByKey(elementKey);

      // Update text format
      setIsBold(selection.hasFormat("bold"));
      setIsItalic(selection.hasFormat("italic"));
      setIsUnderline(selection.hasFormat("underline"));
      setIsCode(selection.hasFormat("code"));

      // Update links
      const node = getSelectedNode(selection);
      const parent = node.getParent();
      if ($isLinkNode(parent) || $isLinkNode(node)) {
        setIsLink(true);
      } else {
        setIsLink(false);
      }

      const tableNode = $findMatchingParent(node, $isTableNode);
      if ($isTableNode(tableNode)) {
        setRootType("table");
      } else {
        setRootType("root");
      }

      if (elementDOM !== null) {
        setSelectedElementKey(elementKey);
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType<ListNode>(anchorNode, ListNode);
          const type = parentList ? parentList.getListType() : element.getListType();
          setBlockType(type);
        } else {
          const type = $isHeadingNode(element) ? element.getTag() : element.getType();
          if (type in blockTypeToBlockName) {
            setBlockType(type as keyof typeof blockTypeToBlockName);
          }
          if ($isCodeNode(element)) {
            const language = element.getLanguage() as keyof typeof CODE_LANGUAGE_MAP;
            setCodeLanguage(language ? CODE_LANGUAGE_MAP[language] || language : "");
            return;
          }
        }
      }
      // Handle buttons
      setFontColor($getSelectionStyleValueForProperty(selection, "color") || "#000000");
      setBgColor($getSelectionStyleValueForProperty(selection, "background-color") || "#fff");
      // setFontFamily($getSelectionStyleValueForProperty(selection, "font-family", "Arial"));
      let matchingParent;
      if ($isLinkNode(parent)) {
        // If node is a link, we need to fetch the parent paragraph node to set format
        matchingParent = $findMatchingParent(node, (parentNode) => $isElementNode(parentNode) && !parentNode.isInline());
      }
    }
  }, [activeEditor, editor]);

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      (_payload, newEditor) => {
        setActiveEditor(newEditor);
        $updateToolbar();
        return false;
      },
      COMMAND_PRIORITY_CRITICAL
    );
  }, [editor, $updateToolbar]);

  useEffect(() => {
    activeEditor.getEditorState().read(() => {
      $updateToolbar();
    });
  }, [activeEditor, $updateToolbar]);

  useEffect(() => {
    return mergeRegister(
      editor.registerEditableListener((editable) => {
        setIsEditable(editable);
      }),
      activeEditor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateToolbar();
        });
      }),
      activeEditor.registerCommand<boolean>(
        CAN_UNDO_COMMAND,
        (payload) => {
          setCanUndo(payload);
          return false;
        },
        COMMAND_PRIORITY_CRITICAL
      ),
      activeEditor.registerCommand<boolean>(
        CAN_REDO_COMMAND,
        (payload) => {
          setCanRedo(payload);
          return false;
        },
        COMMAND_PRIORITY_CRITICAL
      )
    );
  }, [$updateToolbar, activeEditor, editor]);

  const applyStyleText = useCallback(
    (styles: Record<string, string>, skipHistoryStack?: boolean) => {
      activeEditor.update(
        () => {
          const selection = $getSelection();
          if (selection !== null) {
            $patchStyleText(selection, styles);
          }
        },
        skipHistoryStack ? { tag: "historic" } : {}
      );
    },
    [activeEditor]
  );

  const clearFormatting = useCallback(() => {
    activeEditor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection) || $isTableSelection(selection)) {
        const anchor = selection.anchor;
        const focus = selection.focus;
        const nodes = selection.getNodes();
        const extractedNodes = selection.extract();

        if (anchor.key === focus.key && anchor.offset === focus.offset) {
          return;
        }

        nodes.forEach((node, idx) => {
          // We split the first and last node by the selection
          // So that we don't format unselected text inside those nodes
          if ($isTextNode(node)) {
            // Use a separate variable to ensure TS does not lose the refinement
            let textNode = node;
            if (idx === 0 && anchor.offset !== 0) {
              textNode = textNode.splitText(anchor.offset)[1] || textNode;
            }
            if (idx === nodes.length - 1) {
              textNode = textNode.splitText(focus.offset)[0] || textNode;
            }
            /**
             * If the selected text has one format applied
             * selecting a portion of the text, could
             * clear the format to the wrong portion of the text.
             *
             * The cleared text is based on the length of the selected text.
             */
            // We need this in case the selected text only has one format
            const extractedTextNode = extractedNodes[0];
            if (nodes.length === 1 && $isTextNode(extractedTextNode)) {
              textNode = extractedTextNode;
            }

            if (textNode.__style !== "") {
              textNode.setStyle("");
            }
            if (textNode.__format !== 0) {
              textNode.setFormat(0);
              $getNearestBlockElementAncestorOrThrow(textNode).setFormat("");
            }
            node = textNode;
          } else if ($isHeadingNode(node)) {
            node.replace($createSlidParagraphNode(), true);
          } else if ($isDecoratorBlockNode(node)) {
            node.setFormat("");
          }
        });
      }
    });
  }, [activeEditor]);

  const onFontColorSelect = useCallback(
    (value: string, skipHistoryStack: boolean) => {
      applyStyleText({ color: value }, skipHistoryStack);
    },
    [applyStyleText]
  );

  const onBgColorSelect = useCallback(
    (value: string, skipHistoryStack: boolean) => {
      applyStyleText({ "background-color": value }, skipHistoryStack);
    },
    [applyStyleText]
  );

  const insertLink = useCallback(() => {
    if (!isLink) {
      activeEditor.dispatchCommand(TOGGLE_LINK_COMMAND, sanitizeUrl("https://"));
      setIsLinkEditMode(true);
    } else {
      setIsLinkEditMode(false);
      activeEditor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    }
  }, [activeEditor, isLink, setIsLinkEditMode]);

  const onCodeLanguageSelect = useCallback(
    (value: string) => {
      activeEditor.update(() => {
        if (selectedElementKey !== null) {
          const node = $getNodeByKey(selectedElementKey);
          if ($isCodeNode(node)) {
            node.setLanguage(value);
          }
        }
      });
    },
    [activeEditor, selectedElementKey]
  );

  return (
    <Container>
      {/* <button
        disabled={!canUndo || !isEditable}
        onClick={() => {
          activeEditor.dispatchCommand(UNDO_COMMAND, undefined);
        }}
        title={IS_APPLE ? "Undo (⌘Z)" : "Undo (Ctrl+Z)"}
        type="button"
        className="toolbar-item spaced"
        aria-label="Undo"
      >
        <i className="format undo" />
      </button>
      <button
        disabled={!canRedo || !isEditable}
        onClick={() => {
          activeEditor.dispatchCommand(REDO_COMMAND, undefined);
        }}
        title={IS_APPLE ? "Redo (⇧⌘Z)" : "Redo (Ctrl+Y)"}
        type="button"
        className="toolbar-item"
        aria-label="Redo"
      >
        <i className="format redo" />
      </button>
      <Divider />*/}
      {blockType in blockTypeToBlockName && activeEditor === editor && blockType !== "code" && (
        <>
          <BlockFormatDropDown disabled={!isEditable} blockType={blockType} editor={activeEditor} />
          <Divider />
        </>
      )}
      {blockType === "code" ? (
        <DropDown disabled={!isEditable} buttonClassName="toolbar-item code-language" buttonLabel={getLanguageFriendlyName(codeLanguage)} buttonAriaLabel="Select language">
          {CODE_LANGUAGE_OPTIONS.map(([value, name]) => {
            return (
              <DropDownItem className={`item ${dropDownActiveClass(value === codeLanguage)}`} onClick={() => onCodeLanguageSelect(value)} key={value}>
                <span className="text">{name}</span>
              </DropDownItem>
            );
          })}
        </DropDown>
      ) : (
        <>
          <Tooltip title={t("bold")} description={SHORTCUTS.BOLD}>
            <ToolbarButton
              disabled={!isEditable}
              onClick={() => {
                activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold");
              }}
              className={"toolbar-item spaced"}
              type="button"
              aria-label={`Format text as bold. Shortcut: ${SHORTCUTS.BOLD}`}
            >
              <BoldIcon color={isBold ? "var(--blue7)" : "var(--gray17)"} />
            </ToolbarButton>
          </Tooltip>
          <Tooltip title={t("italic")} description={SHORTCUTS.ITALIC}>
            <ToolbarButton
              disabled={!isEditable}
              onClick={() => {
                activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic");
              }}
              className={"toolbar-item spaced "}
              type="button"
              aria-label={`Format text as italics. Shortcut: ${SHORTCUTS.ITALIC}`}
            >
              <ItalicIcon color={isItalic ? "var(--blue7)" : "var(--gray17)"} />
            </ToolbarButton>
          </Tooltip>
          <Tooltip title={t("underline")} description={SHORTCUTS.UNDERLINE}>
            <ToolbarButton
              disabled={!isEditable}
              onClick={() => {
                activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline");
              }}
              className={"toolbar-item spaced "}
              type="button"
              aria-label={`Format text to underlined. Shortcut: ${SHORTCUTS.UNDERLINE}`}
            >
              <UnderlineIcon color={isUnderline ? "var(--blue7)" : "var(--gray17)"} />
            </ToolbarButton>
          </Tooltip>
          <DropdownTextColorPicker
            disabled={!isEditable}
            buttonClassName="toolbar-item color-picker"
            buttonAriaLabel="Formatting text color"
            buttonIconClassName="icon font-color"
            buttonIcon={<TextColorIcon color={fontColor} />}
            color={fontColor}
            onChange={onFontColorSelect}
            title="text color"
            showChevron={true}
          />
          <DropdownBgColorPicker
            disabled={!isEditable}
            buttonClassName="toolbar-item color-picker"
            buttonAriaLabel="Formatting background color"
            buttonIconClassName="icon bg-color"
            buttonIcon={<MarkerIcon color={bgColor} />}
            color={bgColor}
            onChange={onBgColorSelect}
            title="bg color"
            showChevron={true}
          />
          <Tooltip title={t("link")} description={SHORTCUTS.INLINE_LINK}>
            <ToolbarButton disabled={!isEditable} onClick={insertLink} className={"toolbar-item spaced"} aria-label="Insert link" type="button">
              <InlineLinkIcon color={isLink ? "var(--blue7)" : "var(--gray17)"} />
            </ToolbarButton>
          </Tooltip>
          <Tooltip title={t("inline-code")} description={SHORTCUTS.INLINE_CODE}>
            <ToolbarButton
              disabled={!isEditable}
              onClick={() => {
                activeEditor.dispatchCommand(FORMAT_TEXT_COMMAND, "code");
              }}
              className={"toolbar-item spaced"}
              type="button"
              aria-label="Insert code block"
            >
              <InlineCodeIcon color={isCode ? "var(--blue7)" : "var(--gray17)"} />
            </ToolbarButton>
          </Tooltip>
          {/* <DropDown
            disabled={!isEditable}
            buttonClassName="toolbar-item spaced"
            buttonLabel=""
            buttonAriaLabel="Formatting options for additional text styles"
            buttonIconClassName="icon dropdown-more"
          >
            <DropDownItem onClick={clearFormatting} className="item" title="Clear text formatting" aria-label="Clear all text formatting">
              <i className="icon clear" />
              <span className="text">Clear Formatting</span>
            </DropDownItem>
          </DropDown> */}
          <Divider />
          <DropDown
            disabled={!isEditable}
            buttonClassName="toolbar-item spaced"
            buttonLabel={t("insert")}
            buttonLabelIcon={<AddIcon />}
            buttonAriaLabel="Insert specialized editor node"
            buttonIconClassName="icon plus"
          >
            <DropDownItem
              onClick={() => {
                showModal(t("table.title", { keySeparator: ".", ns: "EditorModal" }), (onClose) => <InsertTableDialog activeEditor={activeEditor} onClose={onClose} />);
              }}
              className="item"
            >
              <TableIcon />
              <Typography13 text={t("table")} color="--gray15" />
            </DropDownItem>
            <DropDownItem
              onClick={() => {
                activeEditor.dispatchCommand(INSERT_HORIZONTAL_RULE_COMMAND, undefined);
              }}
              className="item"
            >
              <DividerIcon />
              <Typography13 text={t("divider")} color="--gray15" />
            </DropDownItem>
            <DropDownItem
              onClick={() => {
                showModal(t("image.title", { keySeparator: ".", ns: "EditorModal" }), (onClose) => <InsertImageDialog activeEditor={activeEditor} onClose={onClose} />);
              }}
              className="item"
            >
              <ImageIcon />
              <Typography13 text={t("image")} color="--gray15" />
            </DropDownItem>

            <DropDownItem
              onClick={() => {
                showModal(t("link-preview.title", { keySeparator: ".", ns: "EditorModal" }), (onClose) => <InsertLinkPreviewDialog activeEditor={activeEditor} onClose={onClose} />);
              }}
              className="item"
            >
              <LinkPreviewIcon />
              <Typography13 text={t("link-preview")} color="--gray15" />
            </DropDownItem>
            <DropDownItem
              onClick={() => {
                focusEditor();
                editor.update(() => {
                  let selection = $getSelection();
                  if (selection !== null) {
                    const textContent = selection.getTextContent();
                    const codeNode = $createCodeNode();
                    selection.insertNodes([codeNode]);
                    selection = $getSelection();
                    if ($isRangeSelection(selection)) {
                      selection.insertRawText(textContent);
                    }
                  }
                });
              }}
              className="item"
            >
              <CodeBlockIcon />
              <Typography13 text={t("code-block")} color="--gray15" />
            </DropDownItem>
            <DropDownItem
              onClick={() => {
                showModal(t("equation.title", { keySeparator: ".", ns: "EditorModal" }), (onClose) => <InsertEquationDialog activeEditor={activeEditor} onClose={onClose} />);
              }}
              className="item"
            >
              <MathIcon />
              <Typography13 text={t("math")} color="--gray15" />
            </DropDownItem>
            <DropDownItem
              onClick={() => {
                handleClickPDFUpload();
              }}
              className="item"
            >
              <ImportPdfIcon />
              <Typography13 text={t("import-pdf")} color="--gray15" />
            </DropDownItem>
          </DropDown>
        </>
      )}

      {modal}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  background: #fff;
  padding: 6px 28px;
  align-items: center;
  justify-content: flex-start;
  border-bottom: 1px solid var(--gray3);
  width: 100%;
  min-height: 44px;
  flex-shrink: 0;
  position: absolute;
  top: 56px;
  z-index: 1;
  gap: 8px;
  flex-wrap: wrap;

  @media (max-width: 799px) {
    padding: 6px 12px;
  }
`;

const Divider = styled.div`
  width: 1px;
  height: 24px;
  background: var(--gray3);
  margin: 0 4px;
  flex-shrink: 0;
`;

const HorizontalDivider = styled.div`
  height: 1px;
  width: 100%;
  background: var(--gray3);
  margin: 4px 0;
`;

const ToolbarButton = styled.button`
  /* s of reset button */
  background: inherit;
  border: none;
  box-shadow: none;
  -webkit-tap-highlight-color: transparent;
  box-sizing: border-box;
  outline-style: none;
  padding: 0;
  margin: 0;

  &:focus {
    outline: 0;
    border: none;
  }
  /* e of reset button  */
`;
